/**
 * custom.js — Master animation file
 * ──────────────────────────────────────────────────────────────
 * ORGANISATION PATTERN:
 *
 * Every feature is wrapped in an IIFE with a DOM guard at the
 * top that returns early if the required element(s) are absent.
 * This means each block is self-contained and safe to run on
 * ANY page — no errors from missing elements.
 *
 *   ;(function featureName() {
 *     const el = document.querySelector('.required-element');
 *     if (!el) return;           // ← guard: bail if not on this page
 *     // ... feature code ...
 *   })();
 *
 * The semicolons before each IIFE prevent ASI issues when
 * files are concatenated.
 * ──────────────────────────────────────────────────────────────  
 */ 
  
import gsap from '../../js/gsap/gsap.min.js';   
import { ScrollTrigger } from '../../js/gsap/ScrollTrigger.min.js'
window.__ST_custom = ScrollTrigger;  
import { lenis } from '../../js/theme/_lenis.js';   
import "../../js/theme/_swipers.js";
 
import { SplitText } from '../../js/gsap/SplitText.min.js';
import { ScrollToPlugin } from '../../js/gsap/ScrollToPlugin.min.js';
import { Flip } from '../../js/gsap/Flip.min.js';
import "../../js/theme/_archive-news.js";
import "../../js/theme/_property-filter.js";
import "../../js/theme/_iconnect-service-area-map.js";
import "../../js/theme/_iconnect-banner.js";
import "../../js/theme/_iconnect-hws-selector.js";
import "../../js/theme/_archive-service-area.js";
import "../../js/theme/_mega-menu.js"; 
import "../../js/theme/_taxonomy-hot-water-system-type.js";    
import Splitting from 'splitting'; 
 
gsap.registerPlugin(SplitText, ScrollTrigger, ScrollToPlugin, Flip);

  let mm = gsap.matchMedia();
ScrollTrigger.config({ ignoreMobileResize: true });

;(function initHeader() {
  const header = document.getElementById("wrapper-navbar");
  if (!header) return; 

  header.classList.add("sticky");

  function addClass()    { header.classList.add('scrolling'); }
  function removeClass() { header.classList.remove('scrolling'); }
  ScrollTrigger.addEventListener('scrollStart', addClass);
  ScrollTrigger.addEventListener('scrollEnd', removeClass);

  const showAnim = gsap.from(header, {
    y: -177,
    paused: true,
    duration: 0.8,
    ease: "power2.out"
  }).progress(1);

  let lastState = "";

  ScrollTrigger.create({
    start: '100px',
    end: () => ScrollTrigger.maxScroll(window) + 10,

    onUpdate: (self) => {
      const state =
        self.scroll() < 100 ? "top" :
        self.direction === -1 ? "up" : "down";

      if (state === lastState) return;
      lastState = state;

      if (state === "top") {
        header.classList.remove('up-scroll');
        showAnim.progress(1);
      } else if (state === "up") {
        showAnim.play();
        header.classList.add('up-scroll');
      } else {
        showAnim.reverse();
        header.classList.remove('up-scroll');
      }
    },

    onLeave: () => showAnim.play()
  });
})();
 

/* ══════════════════════════════════════════════════════════════
   IMAGE PARALLAX
   ══════════════════════════════════════════════════════════════ */
;(function initImageParallax() {
  const containers = gsap.utils.toArray('.parallax-inner');
  if (!containers.length) return;

  containers.forEach(container => {
    const img = container.querySelector('img');
    if (!img) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: container,
        scrub: true,
        pin: false,
      }
    }).fromTo(img, {
      yPercent: -10,
      ease: 'none'
    }, {
      yPercent: 10,
      ease: 'none'
    });
  });
})();


/* ══════════════════════════════════════════════════════════════
   SECTION FADE-IN OBSERVER
   ══════════════════════════════════════════════════════════════ */
;(function initSectionReveal() {
  const animatedEls = document.querySelectorAll(".animated");
  if (!animatedEls.length) return;

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const delay = entry.target.getAttribute('data-delay');
        if (delay) {
          entry.target.style.animationDelay = delay;
        }
        entry.target.classList.add("fadeInUp");
      }
    });
  }, { root: null });

  animatedEls.forEach((el) => revealObserver.observe(el));
})();

 
/* ══════════════════════════════════════════════════════════════
   SPLITTING.JS INIT (bundled — no CDN race condition)
   ══════════════════════════════════════════════════════════════ */
   ;(function initSplitting() {
    const targets = document.querySelectorAll('[data-splitting]');
    if (!targets.length) return;
  
    function runSplitting() {
      try {
        targets.forEach(el => {
          if (el.dataset.split) return;          // idempotent — safe if banner split first
          Splitting({ target: el, by: 'chars' });
          el.dataset.split = '1';
        });
        ScrollTrigger.refresh();
      } catch (err) {
        console.error('[initSplitting] Failed:', err);
      }
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runSplitting, { once: true });
    } else {
      runSplitting();
    }
  })();

/* ══════════════════════════════════════════════════════════════
   BANNER REVEAL (plays immediately — no ScrollTrigger)
   ══════════════════════════════════════════════════════════════ */
   ;(function initBannerReveal() {
    const banners = document.querySelectorAll('.banner-reveal');
    if (!banners.length) return;
  
    function runBanner() {
      banners.forEach(banner => {
        if (banner.dataset.revealed) return;
        banner.dataset.revealed = '1';
  
        /* self-split — works regardless of init order */
        if (!banner.dataset.split) {
          Splitting({ target: banner, by: 'chars' });
          banner.dataset.split = '1';
        }
  
        const chars = banner.querySelectorAll('.char');
        if (!chars.length) return;
  
        gsap.fromTo(chars,
          { opacity: 0, y: 10 },
          { duration: 1, stagger: 0.02, opacity: 1, y: 0, delay: 0.1 }
        );
      });
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runBanner, { once: true });
    } else {
      runBanner();
    }
  })();
  

/* ══════════════════════════════════════════════════════════════
   SCROLL REVEAL (fade, text, image wipe) — flattened
   ══════════════════════════════════════════════════════════════ */
   ;(function initScrollReveal() {
    const revealEls = document.querySelectorAll(".reveal-on-scroll");
    if (!revealEls.length) return;
  
    /* ── Text reveal (staggered chars) ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-animate-text", {
      start: "top bottom-=100px",
      once: true,
      onEnter: (elements) => {
        elements.forEach((element) => {
          const chars = element.querySelectorAll(".char");
          if (!chars.length) return;
          gsap.to(chars, {
            duration: 1,
            stagger: 0.02,
            opacity: 1,
            y: 0
          });
        });
      }
    });
  
    /* ── Fade in ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-fade-in", {
      start: "top bottom-=100px",
      once: true,
      onEnter: (elements) => {
        gsap.to(elements, {
          duration: 0.5,
          stagger: 0.2,          // replaces the old index * 0.2 delay
          opacity: 1,
          y: 0
        });
      }
    });
  
    /* ── Image wipe ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-image-wipe", {
      start: "top bottom-=200px",
      once: true,
      onEnter: (elements) => {
        gsap.to(elements, {
          duration: 0.8,
          stagger: 0.2,          // replaces the old index * 0.2 delay
          clipPath: "inset(0% 0% 0% 0%)",
          ease: "power3.inOut"
        });
      }
    });
  })();


/* ══════════════════════════════════════════════════════════════
   PARALLAX WINDOW
   ══════════════════════════════════════════════════════════════ */
   ;(function initParallaxWindow() {
    const windows = document.querySelectorAll(".parallax-window");
    if (!windows.length) return;
  
    windows.forEach(e => {
      const t = e.querySelector(".parallax-image");
      if (!t) return;
      gsap.to(t, {
        scrollTrigger: {
          trigger: e,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true,   // ← recalc positions on refresh
          //markers: true
        },
        yPercent: 20,
        ease: "none",
        
      });
    });
  })();


/* ══════════════════════════════════════════════════════════════
   IMAGE WIPE INITIAL STATE
   ══════════════════════════════════════════════════════════════ */
;(function initImageWipeState() {
  const wipes = document.querySelectorAll(".reveal-image-wipe");
  if (!wipes.length) return;

  gsap.set(wipes, { clipPath: "inset(0% 0% 100% 0%)" });
})();


/* ══════════════════════════════════════════════════════════════
   PRETITLE CHARACTER ANIMATION
   ══════════════════════════════════════════════════════════════ */
;(function initPretitles() {
  const elements = document.querySelectorAll(".animateText [data-animation='pretitle']");
  if (!elements.length) return;

  function animatePretitles() {
    elements.forEach((element) => {
      element.classList.add("pretitle-animation");

      const split = new SplitText(element, {
        type: "chars, lines",
        charsClass: "char",
      });

      let lastDelay = 0;
      split.chars.forEach((char, index) => {
        char.style.transitionDelay = `${0.015 * index}s`;
        lastDelay = 0.015 * index;
      });

      const cleanupTime = (lastDelay + 2.4) * 1000;

      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            split.chars.forEach((char) => char.classList.add("active-animation"));
            setTimeout(() => split.revert(), cleanupTime);
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: "0px 0px -8% 0px" });

      observer.observe(element);
    });
  }

  document.addEventListener("DOMContentLoaded", animatePretitles);
})();


;(function initLetterOpacity() {
  const elements = document.querySelectorAll(
    ".letterAnimation [data-animation='letter-opacity']"
  );
  if (!elements.length) return;

  function createAnimation(element) {
    const split = new SplitText(element, {
      type: "chars, words, lines",
      charsClass: "letter-char",
    });

    gsap.set(split.chars, { opacity: 0.2 });

    const isTeamTop = element.closest(".team-top");

    gsap.to(split.chars, {
      opacity: 1,
      stagger: 0.05,
      ease: "none",
      scrollTrigger: {
        trigger: element,
        start: isTeamTop ? "top 30%" : "top 80%",
        end: isTeamTop ? "bottom center" : "top 20%",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    return split;
  }

  window.addEventListener("load", () => {
    gsap.matchMedia().add("(min-width: 992px)", () => {
      const splits = [];
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              splits.push(createAnimation(entry.target));
            }
          });
        },
        { rootMargin: "200px 0px" }
      );

      elements.forEach((el) => observer.observe(el));

      return () => {
        observer.disconnect();
        splits.forEach((s) => s.revert());
      };
    });
  });
})();


/* ══════════════════════════════════════════════════════════════
   ANIMATION CONTAINER LIFT (parallax on reveal)
   ══════════════════════════════════════════════════════════════ */
   ;(function initAnimationContainerLift() {
    const mm = gsap.matchMedia();
  
    mm.add("(min-width: 600px)", () => {
      gsap.utils.toArray(".animation-container").forEach((container) => {
        const hero = container.closest(".collaborations-hero");
        const endEl =
          hero?.nextElementSibling?.classList.contains("home-collections-listings")
            ? hero.nextElementSibling
            : container.parentElement.querySelector(".home-collections-listings");
  
        gsap.fromTo(
          container,
          { y: 50 },
          {
            y: 0,
            ease: "power1.out",
            scrollTrigger: {
              trigger: container,
              start: "top 85%",
              endTrigger: endEl || container,
              end: "top 40%",
              scrub: 0.8,
              invalidateOnRefresh: true,
            },
          }
        );
      });
    });
  })();

/* ══════════════════════════════════════════════════════════════
   VIDEO BLOCK (pinned reveal — responsive, multi-instance)
   ══════════════════════════════════════════════════════════════ */
   ;(function initVideoBlocks() {
    'use strict';
  
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('[video_block] GSAP / ScrollTrigger not found — aborting init.');
      return;
    }
  
    /**
     * Responsive configuration.
     * Values interpolate linearly between minWidth and maxWidth,
     * clamped at both ends.
     */
    const CONFIG = {
      minWidth: 600,    // smallest supported viewport (px)
      maxWidth: 1440,   // desktop reference viewport (px)
      clipMin: 12,      // clip-path inset (%) at minWidth
      clipMax: 20,      // clip-path inset (%) at maxWidth
      heightMin: 90,    // container height (vh) at minWidth
      heightMax: 100,   // container height (vh) at maxWidth
      wrapperMultiplier: 2, // wrapper height = container height × this (scrub distance)
    };
  
    /**
     * Linearly interpolate a value based on current viewport width.
     * @param {number} min - Value at CONFIG.minWidth.
     * @param {number} max - Value at CONFIG.maxWidth.
     * @returns {number} Interpolated value, clamped to [min, max].
     */
    function fluidValue(min, max) {
      const vw = window.innerWidth;
      const t = (vw - CONFIG.minWidth) / (CONFIG.maxWidth - CONFIG.minWidth);
      return min + (max - min) * Math.min(1, Math.max(0, t));
    }
  
    /** @returns {number} Current clip inset percentage for this viewport. */
    function getClipInset() {
      return fluidValue(CONFIG.clipMin, CONFIG.clipMax);
    }
  
    /** @returns {number} Current container height in vh for this viewport. */
    function getContainerVh() {
      return fluidValue(CONFIG.heightMin, CONFIG.heightMax);
    }
  
    const wrappers = gsap.utils.toArray('.video_block_wrapper');
    if (!wrappers.length) return;
  
    /**
     * Write responsive CSS custom properties onto each wrapper.
     * Runs before every ScrollTrigger measurement pass (refreshInit),
     * so pin/scrub distances are always based on fresh dimensions.
     */
    function applyResponsiveSizing() {
      const vh = getContainerVh();
      wrappers.forEach((wrapper) => {
        wrapper.style.setProperty('--vb-height', vh + 'vh');
        wrapper.style.setProperty(
          '--vb-wrapper-height',
          vh * CONFIG.wrapperMultiplier + 'vh'
        );
      });
    }
  
    // Ensure sizing is correct BEFORE ScrollTrigger measures (initial + every refresh).
    ScrollTrigger.addEventListener('refreshInit', applyResponsiveSizing);
    applyResponsiveSizing();
  
    wrappers.forEach((wrapper) => {
      try {
        const container       = wrapper.querySelector('.video_block_container');
        const slidesContainer = wrapper.querySelector('.video_block_slidesContainer');
        const image           = wrapper.querySelector('.styles_image');
        const textContainer   = wrapper.querySelector('.video_block_textContainer');
  
        if (!container || !slidesContainer || !image) return; // skip incomplete instances
  
        gsap.set(image, { scale: 1.15 });
        gsap.set(slidesContainer, {
          clipPath: () => 'inset(' + getClipInset() + '%)',
        });
  
        if (textContainer) {
          gsap.set(textContainer, { autoAlpha: 0, y: 20 });
        }
  
        const tl = gsap.timeline();
  
        tl.fromTo(
          slidesContainer,
          // Function-based start value — re-evaluated on every refresh
          // thanks to invalidateOnRefresh, so resizing mid-page recalculates
          // the correct inset for the new viewport width.
          { clipPath: () => 'inset(' + getClipInset() + '%)' },
          { clipPath: 'inset(0%)', ease: 'none', duration: 1 },
          0
        );
  
        tl.to(image, { scale: 1, ease: 'none', duration: 1 }, 0);
  
        if (textContainer) {
          tl.to(
            textContainer,
            { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.3 },
            0.35
          );
        }
  
        ScrollTrigger.create({
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          pin: container,
          scrub: true,
          animation: tl,
          invalidateOnRefresh: true, // re-reads function-based values on resize
        });
      } catch (err) {
        console.error('[video_block] Failed to initialise instance:', err, wrapper);
      }
    });
  })();

/* ══════════════════════════════════════════════════════════════
   SCROLLTRIGGER REFRESH (after layout settles)
   ══════════════════════════════════════════════════════════════ */
   ;(function refreshScrollTriggers() {
    // baseline refresh once the load event fires
    window.addEventListener("load", () => ScrollTrigger.refresh());
  
    // re-refresh as lazy/late images come in (debounced)
    let timer;
    const queueRefresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => ScrollTrigger.refresh(), 200);
    };
  
    document.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", queueRefresh, { once: true });
        img.addEventListener("error", queueRefresh, { once: true });
      }
    });
  })();

